import React, { useEffect, useState } from "react";
import createNoteStyles from "./createNoteStyles";
import { Box, Checkbox, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createResource,
  fetchCollection,
  queryKeys,
  updateResource,
} from "../../../services/ReactQuery/reactQueryService";
import { Note } from "../../../types/Note";
import LegalbirdTipTapEditor from "../../LbEditor/LegalbirdTipTapEditor";
import LegalbirdTipTapEditorProvider from "../../LbEditor/LegalbirdTipTapEditorProvider";
import { useCurrentEditor } from "@tiptap/react";
import { getEditorHtml } from "../../../services/Editor/editorFunctions";
import { ProductUnion } from "../../../types/ProductUnion";
import { createPortal } from "react-dom";
import { useDrawer } from "../../../provider/DrawerProvider";
import useDraftAutosave from "../../../hooks/useDraftAutosave";
import useEffectWithoutMount from "../../../hooks/useEffectWithoutMount";
import DraftAutosaveIndicator from "../../GlobalDrawer/DraftAutosaveIndicator";

type EditNoteProps = {
  closeDialog: () => void;
  product: ProductUnion;
  note?: Note;
  overwrittenHandleSave?: Function;
};

const EditNote = (props: EditNoteProps) => {
  return (
    <LegalbirdTipTapEditorProvider placeholder={"Notiz"}>
      <EditNoteInner {...props} />
    </LegalbirdTipTapEditorProvider>
  );
};

const EditNoteInner = ({ closeDialog, product, note, overwrittenHandleSave }: EditNoteProps) => {
  const queryClient = useQueryClient();
  const [pinned, setPinned] = useState(!!note ? note.pinned : true);
  const [isWarning, setIsWarning] = useState(!!note ? !!note.isWarning : false);
  const createMutation = useMutation({
    mutationFn: createResource
  });
  const updateMutation = useMutation({
    mutationFn: updateResource
  });
  const { editor } = useCurrentEditor();
  const { drawerActionsNode, drawerHeaderNode } = useDrawer();

  useEffect(() => {
    if (note?.content) {
      editor?.commands.setContent(note.content);
    }
  }, []);

  const pinnedNotesFilters = {
    case: product.backofficeCase["@id"],
    pinned: true,
  };
  const { data: pinnedNotes } = useQuery({
    queryKey: queryKeys.collection("notes", pinnedNotesFilters),

    queryFn: () =>
      fetchCollection("notes", pinnedNotesFilters)
  });

  const buildNoteData = () => ({
    content: editor ? getEditorHtml(editor) : "",
    pinned: pinned,
    isWarning: isWarning,
  });

  const { saveState, scheduleSave } = useDraftAutosave({
    enabled: !!overwrittenHandleSave,
    persist: async () => {
      await overwrittenHandleSave?.(buildNoteData());
    },
    editor,
  });

  useEffectWithoutMount(() => scheduleSave(), [pinned, isWarning]);

  if (!pinnedNotes || !editor || !drawerActionsNode) {
    return null;
  }

  const pinnedNotesCount = pinnedNotes["hydra:totalItems"];

  const existingPinnedInNotes = !note
    ? false
    : pinnedNotes["hydra:member"].some((pinnedNote: Note) => pinnedNote.id === note.id);

  let pinnedLimitExceeded = false;

  if (pinnedNotesCount >= 4 && pinned && !existingPinnedInNotes) {
    pinnedLimitExceeded = true;
  }

  const saveNote = async () => {
    //draft mode must never mutate notes directly; persistence runs through the autosave (see EditActivity for the discussion)
    if (overwrittenHandleSave) {
      return;
    }
    const noteData = buildNoteData();

    if (!product.backofficeCase?.["@id"]) {
      return;
    }
    if (note && note.id) {
      await updateMutation.mutateAsync({
        id: note.id,
        uri: "notes",
        data: {
          ...noteData,
          case: product.backofficeCase["@id"],
        },
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.collections("notes")
      });
      closeDialog();
      return;
    }

    await createMutation.mutateAsync({
      uri: "notes",
      data: {
        ...noteData,
        case: product.backofficeCase["@id"],
      },
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collections("notes")
    });
    internalCloseDialog();
  };

  const internalCloseDialog = () => {
    if (!note) {
      setPinned(true);
      setIsWarning(false);
      editor.commands.clearContent();
    }
    closeDialog();
  };

  const saveDisabled =
    pinnedLimitExceeded ||
    (!!note
      ? note.pinned === pinned && note.isWarning === isWarning && getEditorHtml(editor) === note.content
      : editor.isEmpty);

  if (pinnedLimitExceeded) {
    return (
      <Box>
        Es ist bereits die maximal mögliche Anzahl an Notizen am Fall angeheftet. Bitte vorhandene Notizen abheften oder
        aktualisieren. Sobald weniger als 4 Notizen am Fall sind, kann eine neue Notiz angelegt werden.
      </Box>
    );
  }

  return (
    <>
      <Grid container>
        <Grid size={12}>
          <LegalbirdTipTapEditor editorSx={{ backgroundColor: "background.note" }} />
        </Grid>
        <Grid size={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={pinned}
                onChange={({ target }) => setPinned(target.checked)}
                sx={createNoteStyles.checkbox}
              />
            }
            label="Notiz am Fall anheften"
          />
        </Grid>
        <Grid size={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isWarning}
                onChange={({ target }) => setIsWarning(target.checked)}
                sx={createNoteStyles.checkbox}
              />
            }
            label="Als Warnung anzeigen"
          />
        </Grid>
      </Grid>
      {overwrittenHandleSave
        ? drawerHeaderNode && createPortal(<DraftAutosaveIndicator saveState={saveState} />, drawerHeaderNode)
        : createPortal(
            <Grid container sx={{ width: "100%" }}>
              <Grid size={3} sx={{ display: { xs: "none", md: "block" } }} />
              <Grid size={{ xs: 12, md: 6 }} sx={{ marginTop: 6 }}>
                <Button variant={"contained"} onClick={saveNote} disabled={saveDisabled}>
                  Speichern
                </Button>
              </Grid>
            </Grid>,
            drawerActionsNode
          )}
    </>
  );
};
export default EditNote;
